<template>
  <AppWithLoading class="ma-auto" v-slot:default="{ loader }" :fetch="fetchData">
    <template v-if="currentUser && company">
      <v-card
        tile
        flat
        max-width="540"
        color="white"
        class="pa-8 ma-auto"
      >
        <v-card-title class="mx-0 mb-8 pa-0">
          {{ $t('pageTitle', { companyName: company.name }) }}
        </v-card-title>
        <VForm autocomplete="off" @submit.prevent>
          <VTextField
            id="firstName"
            v-model="data.firstName"
            outlined
            dense
            placeholder=" "
            :error-messages="errors.firstName"
          >
            <template v-slot:label>
              {{ $t('firstName') }} *
            </template>
          </VTextField>
          <VTextField
            id="lastName"
            v-model="data.lastName"
            outlined
            dense
            placeholder=" "
            :error-messages="errors.lastName"
          >
            <template v-slot:label>
              {{ $t('lastName') }} *
            </template>
          </VTextField>
          <PhoneNumberInput
            id="phone"
            v-model="data.phone"
            :key="countryCode ? countryCode: 'RU'"
            :default-country="countryCode ? countryCode: null"
            :auto-default-country="false"
            required
            :error-messages="errors['phone']"
            @input="errors['phone'] = null"
          />
          <VTextField
            id="position"
            v-model="data.position"
            outlined
            dense
            placeholder=" "
            :error-messages="errors.position"
          >
            <template v-slot:label>
              {{ $t('position') }} *
            </template>
          </VTextField>
          <VTextField
            id="newPassword"
            v-model="data.newPassword"
            outlined
            dense
            placeholder=" "
            type="password"
            :error-messages="errors.newPassword"
          >
            <template v-slot:label>
              {{ $t('newPassword') }} *
            </template>
          </VTextField>
          <VTextField
            id="newPasswordConfirmation"
            v-model="data.newPasswordConfirmation"
            outlined
            dense
            placeholder=" "
            type="password"
            :error-messages="errors.newPasswordConfirmation"
          >
            <template v-slot:label>
              {{ $t('newPasswordConfirmation') }} *
            </template>
          </VTextField>
        </VForm>
        <v-alert v-if="hasErrorMessageOnly" class="my-2 red--text text-center">
          {{ errorMessage }}
        </v-alert>
        <v-card-actions class="mx-0 px-0">
          <VBtn
            @click="loader(() => change())"
            id="save-changes-button"
            color="success"
            dark
            class="ml-0"
            elevation="0"
            type="submit"
          >
            {{ $t('join') }}
          </VBtn>
          <VBtn
            @click="logout"
            id="logout-button"
            color="info"
            dark
            elevation="0"
            type="submit"
          >
            {{ $t('logout') }}
          </VBtn>
        </v-card-actions>
      </v-card>
    </template>
  </AppWithLoading>
</template>

<script>
import useUser from '@/compositions/useUser';
import AppWithLoading from '@/components/AppWithLoading';
import useValidation from '@/compositions/useValidation';
import { reactive, ref, toRefs } from '@vue/composition-api';
import { response } from '@/orm/functions';
import Country from '@/orm/models/Country/Country';
import PhoneNumberInput from '@/components/Input/PhoneNumberInput';
import { normalizePhoneNumber } from '@/functions';
import ValidationMixin from '../mixins/ValidationMixin';
import PageTitle from '../components/PageTitle.vue';

export default {
  name: 'ChangeTempPasswordByUniqueLink',
  components: {
    AppWithLoading,
    PageTitle,
    PhoneNumberInput,
  },
  mixins: [ValidationMixin],
  props: {
    uid: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      data: {
        firstName: null,
        lastName: null,
        phone: null,
        position: null,
        newPassword: null,
        newPasswordConfirmation: null,
      },
    };
  },
  setup(props, { root: { $router } }) {
    const { uid } = toRefs(props);
    const {
      updateNewUser, fetchUserByCreatedUid, users,
    } = useUser();
    const currentUser = ref(null);
    const company = ref(null);

    const {
      withValidationHandling,
    } = useValidation();

    const countries = reactive([]);
    const countryCode = ref(null);

    async function loadCountries() {
      await withValidationHandling(async () => {
        countries.push(...await response(Country.api().fetch()));
      });
    }
    async function redirectToLogin() {
      await $router.push({ name: 'login' });
    }
    async function fetchData() {
      try {
        await fetchUserByCreatedUid(uid.value);
      } catch (exception) {
        await redirectToLogin();
      }
      await loadCountries();
      currentUser.value = users.value[0] || null;

      if (currentUser.value && !currentUser?.value?.createdUid) {
        await redirectToLogin();
      }

      company.value = currentUser.value.company;
      countryCode.value = company?.value?.country?.alpha2Code;
    }

    return {
      updateNewUser,
      loadCountries,
      countries,
      countryCode,
      company,
      fetchData,
      currentUser,
      redirectToLogin,
    };
  },
  methods: {
    async change() {
      await this.withValidationHandling(async () => {
        await this.updateNewUser(this.uid, {
          firstName: this.data.firstName,
          lastName: this.data.lastName,
          phone: normalizePhoneNumber(this.data.phone),
          position: this.data.position,
          newPassword: this.data.newPassword,
          newPasswordConfirmation: this.data.newPasswordConfirmation,
        });
      });

      if (!this.hasErrors) {
        await this.redirectToLogin();
      }
    },
    async logout() {
      await this.redirectToLogin();
    },
  },
};
</script>

<style scoped>
.v-card__title {
  font-size: 24px;
  line-height: 110%;
}
.v-btn__content {
  font-size: 16px !important;
  padding: 0 15px !important;
}
</style>

<i18n locale="en" lang="yaml">
pageTitle: You have been invited to join the "{companyName}" account
newPassword: New password
newPasswordConfirmation: New password confirmation
join: Join
firstName: First name
lastName: Last name
phone: Phone
position: Position
logout: Logout
</i18n>

<i18n locale="ru" lang="yaml">
pageTitle: Вас пригласили присоединиться к аккаунту "{companyName}"
newPassword: Новый пароль
newPasswordConfirmation: Подтвердите новый пароль
join: Присоединиться
firstName: Имя
lastName: Фамилия
phone: Номер телефона
position: Должность
logout: Выйти
</i18n>
